<template>
  <form
    class="form"
    method="POST"
    action="javascript:;"
    @submit.prevent="submit"
  >
    <FormError v-if="formError.length > 0" :errorMessages="formError" />

    <FormLoading v-if="!formReady" />

    <CardBase v-else>
      <template #header>{{ $route.meta.title }}</template>
      <template #content>
        <FormNote class="mb-6"
          >Note: Sistem tidak akan dapat menambahkan member baru dengan data
          nomor rekening bank atau nomor kontak yang sama.</FormNote
        >
        <h3 class="form__section-heading">1. Member Info</h3>
        <FormRow class="mb-4" :required="true">
          <template #label>Name</template>
          <template #field>
            <input
              :class="{
                'form__input--error': $v.formData.name.$error && formSubmitted,
              }"
              type="text"
              class="form__input"
              placeholder="Name"
              v-model="formData.name"
              v-focus
              @change="nameToAccountHolder"
              :readonly="!userCan('update.member') && !formCreate"
              :disabled="!userCan('update.member') && !formCreate"
            />
            <div
              class="form-error"
              v-if="$v.formData.name.$error && formSubmitted"
            >
              <span class="form-error__text" v-if="!$v.formData.name.required"
                >This field is required.</span
              >
            </div>
          </template>
        </FormRow>

        <FormRow
          class="mb-4"
          v-for="(v, index) in $v.formData.contacts.$each.$iter"
          :key="index"
        >
          <template #label v-if="index == 0">Contact Info</template>
          <template #field>
            <div class="flex justify-between">
              <div :class="formData.contacts.length > 1 ? 'w-5/12' : 'w-1/2'">
                <multiselect
                  v-model="v.type.$model"
                  :options="listMemberContactType"
                  :loading="listMemberContactTypeLoading"
                  :allow-empty="false"
                  :class="v.type.$error ? 'multiselect--error' : ''"
                  class="mr-2 w-5/12"
                  track-by="id"
                  label="name"
                  tag-placeholder="Press enter to choose"
                  :showLabels="false"
                  placeholder="Type"
                  :disabled="formData.contacts[index].id ? true : false"
                ></multiselect>
              </div>
              <input
                :class="[
                  { 'form__input--error': v.value.$error && formSubmitted },
                  formData.contacts.length > 1 ? 'w-5/12' : 'w-1/2',
                  formData.contacts[index].id
                    ? 'bg-gray-400 bg-opacity-25'
                    : '',
                ]"
                type="text"
                class="form__input ml-2"
                v-model="v.value.$model"
                placeholder="Number"
                :readonly="formData.contacts[index].id"
                :disabled="formData.contacts[index].id"
              />
              <BaseButton
                buttonClass="default"
                class="ml-2"
                @click="removeContact(index)"
                v-if="formData.contacts.length > 0 && userCan('delete.member-contact')"
              >
                <font-awesome-icon :icon="['far', 'trash-alt']" />
              </BaseButton>
            </div>
            <div
              class="form-error"
              v-if="(v.type.$error || v.value.$error) && formSubmitted"
            >
              <span
                class="form-error__text"
                v-if="!v.type.required || !v.value.required"
                >These fields are required.</span
              >
            </div>
          </template>
        </FormRow>

        <FormRow class="mb-4">
          <template #label></template>
          <template #field>
            <BaseButton
              type="button"
              @click="addContact"
              buttonClass="secondary"
              v-if="userCan('create.member-contact')"
              >Add Contact Info</BaseButton
            >
          </template>
        </FormRow>

        <FormRow class="mb-4" :required="true">
          <template #label>Category</template>
          <template #field>
            <multiselect
              :allow-empty="false"
              deselectLabel=""
              :options="listBankCategory"
              :loading="listBankCategoryLoading"
              :class="
                $v.formData.bank_category.$error ? 'multiselect--error' : ''
              "
              track-by="id"
              label="name"
              placeholder="Choose category"
              v-model="formData.bank_category"
            >
              <template #noResult>No results found.</template>
            </multiselect>

            <div
              class="form-error"
              v-if="$v.formData.bank_category.$error && formSubmitted"
            >
              <span
                class="form-error__text"
                v-if="!$v.formData.bank_category.required"
                >This field is required.</span
              >
            </div>
          </template>
        </FormRow>

        <div v-if="formCreate">
          <h3 class="form__section-heading">2. Bank Info</h3>

          <FormRow class="mb-4" :required="true">
            <template #label>Bank Group</template>
            <template #field>
              <multiselect
                :allow-empty="false"
                deselectLabel=""
                :options="listBankGroup"
                :loading="listBankGroupLoading"
                :class="
                  $v.formData.bank_group.$error ? 'multiselect--error' : ''
                "
                track-by="id"
                label="name"
                tag-placeholder="Press enter to choose"
                placeholder="Choose bank group"
                v-model="formData.bank_group"
                @input="filterListBank"
              >
                <template #noResult>No results found.</template>
              </multiselect>
              <div
                class="form-error"
                v-if="$v.formData.bank_group.$error && formSubmitted"
              >
                <span
                  class="form-error__text"
                  v-if="!$v.formData.bank_group.required"
                  >This field is required.</span
                >
              </div>
            </template>
          </FormRow>

          <FormRow class="mb-4" :required="true">
            <template #label>Bank</template>
            <template #field>
              <multiselect
                :options="listBank"
                :loading="listBankLoading"
                :class="
                  $v.formData.bank_account.bank.$error
                    ? 'multiselect--error'
                    : ''
                "
                track-by="id"
                label="name"
                :allow-empty="false"
                deselectLabel=""
                tag-placeholder="Press enter to choose"
                placeholder="Choose bank"
                v-model="formData.bank_account.bank"
              >
                <template #noOptions v-if="!formData.bank_group"
                  >Please choose Bank Group</template
                >
                <template #noResult>No results found.</template>
              </multiselect>
              <div
                class="form-error"
                v-if="$v.formData.bank_account.bank.$error && formSubmitted"
              >
                <span
                  class="form-error__text"
                  v-if="!$v.formData.bank_account.bank.required"
                  >This field is required.</span
                >
              </div>
            </template>
          </FormRow>

          <FormRow class="mb-4" :required="true">
            <template #label>Account holder</template>
            <template #field>
              <input
                :class="{
                  'form__input--error':
                    $v.formData.bank_account.holder.$error && formSubmitted,
                }"
                type="text"
                class="form__input"
                placeholder="Account holder name"
                v-model="formData.bank_account.holder"
              />
              <div
                class="form-error"
                v-if="$v.formData.bank_account.holder.$error && formSubmitted"
              >
                <span
                  class="form-error__text"
                  v-if="!$v.formData.bank_account.holder.required"
                  >This field is required.</span
                >
              </div>
            </template>
          </FormRow>

          <FormRow class="mb-4" :required="true">
            <template #label>Account number</template>
            <template #field>
              <input
                :class="{
                  'form__input--error':
                    $v.formData.bank_account.number.$error && formSubmitted,
                }"
                type="text"
                class="form__input"
                placeholder="Account number"
                v-model="formData.bank_account.number"
              />
              <div
                class="form-error"
                v-if="$v.formData.bank_account.number.$error && formSubmitted"
              >
                <span
                  class="form-error__text"
                  v-if="!$v.formData.bank_account.number.required"
                  >This field is required.</span
                >
                <span
                  class="form-error__text"
                  v-if="!$v.formData.bank_account.number.numeric"
                  >Only numbers are allowed.</span
                >
              </div>
            </template>
          </FormRow>

          <h3 class="form__section-heading">3. Account Info</h3>

          <FormNote class="mb-6"
          >Note: Sistem tidak akan dapat menambahkan Panel ID / Username yang
            sama.<br />Untuk Cash Market (CM), cukup isi kolom Panel ID.<br />Untuk
            White Label (WL) / Nexus, kolom username disamakan dengan Panel ID.</FormNote
          >

          <FormRow class="mb-4" :required="true">
            <template #label>Asset</template>
            <template #field>
              <multiselect
                v-model="formData.account.asset"
                :options="listAsset"
                :loading="listAssetLoading"
                :close-on-select="true"
                :hide-selected="false"
                :allow-empty="false"
                :class="
                  $v.formData.account.asset.$error ? 'multiselect--error' : ''
                "
                track-by="id"
                label="name"
                tag-placeholder="Press enter to choose"
                placeholder="Search asset"
                :showLabels="false"
                @select="getListProduct"
              >
                <template #noResult>No results found.</template>
              </multiselect>
              <div
                class="form-error"
                v-if="$v.formData.account.asset.$error && formSubmitted"
              >
                <span
                  class="form-error__text"
                  v-if="!$v.formData.account.asset.required"
                  >This field is required.</span
                >
              </div>
            </template>
          </FormRow>

          <FormRow class="mb-4" :required="true">
            <template #label>Product</template>
            <template #field>
              <multiselect
                v-model="formData.account.product"
                :options="listProduct"
                :loading="listProductLoading"
                :close-on-select="true"
                :hide-selected="false"
                :allow-empty="false"
                :class="
                  $v.formData.account.product.$error ? 'multiselect--error' : ''
                "
                track-by="id"
                label="name_master"
                tag-placeholder="Press enter to choose"
                placeholder="Search product"
                deselectLabel=""
              >
                <template #noOptions v-if="formData.account.asset"
                  >No products found for Asset
                  {{ formData.account.asset.name }}.</template
                >
                <template #noOptions v-else
                  >Please select asset first.</template
                >
                <template #noResult v-if="formData.account.asset"
                  >No results found.</template
                >
                <template #noResult v-else>Please select asset first.</template>
              </multiselect>
              <div
                class="form-error"
                v-if="$v.formData.account.product.$error && formSubmitted"
              >
                <span
                  class="form-error__text"
                  v-if="!$v.formData.account.product.required"
                  >This field is required.</span
                >
              </div>
            </template>
            <template #help
              >Only products for selected asset will be shown.</template
            >
          </FormRow>


          <FormRow class="mb-4" :required="true">
            <template #label>Panel ID</template>
            <template #field>
              <input
                  :class="{
                  'form__input--error':
                    $v.formData.account.panel_id.$error && formSubmitted,
                }"
                  type="text"
                  class="form__input"
                  placeholder="Account ID"
                  v-model="formData.account.panel_id"
              />
              <div
                  class="form-error"
                  v-if="$v.formData.account.panel_id.$error && formSubmitted"
              >
                <span
                    class="form-error__text"
                    v-if="!$v.formData.account.panel_id.required"
                >This field is required.</span
                >
                <span
                    class="form-error__text"
                    v-if="!$v.formData.account.panel_id.alphaNumUnderscore"
                >Only letters, numbers and underscores are allowed.</span
                >
              </div>
            </template>
          </FormRow>

          <FormRow class="mb-4">
            <template #label>Username</template>
            <template #field>
              <input
                type="text"
                class="form__input"
                placeholder="Username"
                v-model="formData.account.username"
              />
              <span
                class="form-error__text"
                v-if="!$v.formData.account.username.alphaNumUnderscore"
                >Only letters, numbers and underscores are allowed.</span
              >
            </template>
            <template #help
            >Only for White Label (WL) / Nexus. Username must be the same as
              Panel ID.</template
            >
          </FormRow>

          <FormRow class="mb-4">
            <template #label>Account Note</template>
            <template #field>
              <textarea
                class="form__input"
                placeholder="Account Note"
                rows="5"
                v-model="formData.account.note"
              ></textarea>
            </template>
          </FormRow>
        </div>

        <FormDataLog :data="formData" v-if="!formCreate" />
      </template>

      <template #footer>
        <FormRow class="mb-0">
          <template #field>
            <BaseButton type="submit">Submit</BaseButton>
          </template>
          <template #link>
            <div class="ml-4 poppins">
              or
              <router-link :to="{ name: 'MemberList' }" class="link ml-2"
                >Back to List Members</router-link
              >
            </div>
          </template>
        </FormRow>
      </template>
    </CardBase>
  </form>
</template>
<style src="vue-multiselect/dist/vue-multiselect.min.css"></style>

<script>
import Multiselect from "vue-multiselect";
import { numeric, required, helpers } from "vuelidate/lib/validators";
import { getListAssetMixin } from "@/mixins/getListAssetMixin";
import { getListBankOptionsMixin } from "@/mixins/getListBankOptionsMixin";
import { getListProductOptionsMixin } from "@/mixins/getListProductOptionsMixin";
import { submitFormMixin } from "@/mixins/submitFormMixin";
const alphaNumUnderscore = helpers.regex(
  "alphaNumUnderscore",
  /^[a-zA-Z\d_]*$/
);

export default {
  mixins: [
    getListAssetMixin,
    getListBankOptionsMixin,
    getListProductOptionsMixin,
    submitFormMixin,
  ],
  components: {
    Multiselect,
  },
  validations() {
    if (this.$route.name == "MemberCreate") {
      return {
        formData: {
          name: {
            required,
          },
          bank_group: {
            required,
          },
          bank_category: {
            required,
          },
          bank_account: {
            bank: { required },
            holder: { required },
            number: { required, numeric },
          },
          account: {
            asset: { required },
            product: { required },
            panel_id: { alphaNumUnderscore, required },
            username: { alphaNumUnderscore },
          },
          contacts: {
            $each: {
              type: {  },
              value: {  },
            },
          },
        },
      };
    } else if (this.$route.name == "MemberEdit") {
      return {
        formData: {
          name: {
            required,
          },
          bank_category: {
            required,
          },
          contacts: {
            $each: {
              type: {  },
              value: {  },
            },
          },
        },
      };
    }
  },
  data: () => {
    return {
      listBankLoading: false,
      listAsset: [],
      listAssetLoading: true,

      listProduct: [],
      listProductLoading: false,

      listMemberContactType: [],
      listMemberContactTypeLoading: true,

      formData: {
        name: null,
        bank_category: null,

        contacts: [],
        bank_account: {
          id: null,
        },
        account: {
          asset: null,
          product: null,
          panel_id: null,
          username: null,
          note: null,
        },
        deleted_contacts: [],
        new_contacts: [],
      },
    };
  },
  created() {
    if (this.formCreate) {
      this.getListAsset();
      this.addContact();
      this.addContact();
    }

    this.getListBankCategory();
    this.getListBankGroup();
    this.getMemberContactType();
  },
  methods: {
    filterListBank(bankGroup) {
      delete this.formData.bank_account.bank;
      this.getListBank(bankGroup.id);
    },
    submit() {
      if (!this.formLoading) {
        this.formSubmitted = true;
        this.$v.$touch();

        if (this.$v.$invalid) {
          return;
        }

        this.formLoading = true;
        this.$store.commit("toggleLoading");

        const method = this.formCreate ? "post" : "patch";
        const url = process.env.VUE_APP_API_URL + this.submitUrl;

        if (!this.formCreate) {
          this.formData.new_contacts = [];
          this.formData.contacts.forEach((item, index) => {
            if (!this.formData.contacts[index].id) {
              // Kalo ga ada ID, berarti yang baru,
              this.formData.new_contacts.push(this.formData.contacts[index]);
            }
          });
        }

        this.$http({
          method: method,
          url: url,
          data: this.formData,
        })
          .then((res) => {
            if (res.status == 200 || res.status == 201) {
              this.$router.push({ name: this.$route.meta.baseRoute }, () => {
                this.$swal({
                  position: "bottom-end",
                  showConfirmButton: false,
                  toast: true,
                  timer: 5000,
                  timerProgressBar: true,
                  title: res.data.message,
                  icon: "success",
                });
              });
            }
          })
          .catch((error) => {
            if (error.response) {
              this.formError = [];

              if (error.response.status == 404) {
                // this.formError[0] = '<pre>' + error.response.data.meta + '</pre>';
              } else if (error.response.status == 409) {
                // this.formError[0] = error.response.data;
              } else {
                const { data } = error.response.data;
                for (var err in data) {
                  this.formError.push(data[err]);
                }
              }
            }
          })
          .finally(() => {
            this.$store.commit("toggleLoading");
            this.formLoading = false;
            this.formSubmitted = false;
          });
      }
    },
    addContact() {
      this.formData.contacts.push({
        type: null,
        value: "",
      });
    },
    removeContact(index) {
      const data = this.formData.contacts[index];
      if (data.id) {
        this.formData.deleted_contacts.push(data.id);
      } else {
        delete this.formData.contacts[index];
      }
      this.formData.contacts.splice(index, 1);
    },
    nameToAccountHolder() {
      this.formData.bank_account.holder = this.formData.name;
    },
    getMemberContactType() {
      this.$http
        .get(process.env.VUE_APP_API_URL + "/member-contact-type")
        .then((res) => {
          if (res.status == 200) {
            const response = res.data;

            if (response) {
              this.listMemberContactType = response.data;
            }
          }
        })
        .finally(() => {
          this.listMemberContactTypeLoading = false;
        });
    },
    fetchData(id) {
      this.formReady = false;

      const itemId = id ? id : this.formData.id;

      this.$http
        .get(
          process.env.VUE_APP_API_URL + "/" + this.fetchEndPoint + "/" + itemId
        )
        .then((res) => {
          if (res.status == 200) {
            const { data } = res.data;
            this.formData = { ...this.formData, ...data };

            console.log(this.formData.contacts);

            // data.contacts.forEach((item, index) => {
            //   this.formData.contacts.push({
            //     member_contact_type: "",
            //     value: "",
            //     member_contact_type_id: null,
            //   });
            // });

            this.formReady = true;
          }
        })
        .catch((err) => {
          if (err.response.status == 404) {
            this.$router.push({ name: this.$route.meta.baseRoute });
          } else if (err.response.status == 500) {
            this.formError = [];
            this.formError[0] = err.response.data.message;
          }
        })
        .finally(() => {});
    },
  },
};
</script>
